// Pure helpers for the Shared Voicemail management page. No React/DOM — unit
// tested with `npx tsx --test src/lib/voicemailBox.test.ts` (ui-src tests run
// locally; CI type-checks ui-src and runs the api suite).

// The five greeting slots on a shared voicemail box, in display order. Each maps
// to a stratus_ext.voicemail_boxes *_greeting_id column that stores a
// custom_prompts.prompt_uuid (the AudioPicker value).
export const GREETING_SLOTS: { key: string; label: string }[] = [
  { key: 'default_greeting_id', label: 'Default (all statuses)' },
  { key: 'vm_greeting_business_id', label: 'Business hours' },
  { key: 'vm_greeting_after_hours_id', label: 'After hours' },
  { key: 'vm_greeting_break_id', label: 'Break' },
  { key: 'vm_greeting_holiday_id', label: 'Holiday' },
];

// Build the API request body for creating/updating a shared box from the editor
// form. Pure so the exact wire shape (incl. the #1251 mwi_light_enabled toggle and
// the null-coalescing of blank optional fields) is unit-tested without React.
export function buildBoxSaveBody(form: any): any {
  const body: any = {
    name: form.name,
    mailbox_id: form.mailbox_id,
    pin: form.pin || null,
    email_to: form.email_to || null,
    members: form.members,
    mwi_light_enabled: form.mwi_light_enabled === true,
  };
  for (const s of GREETING_SLOTS) body[s.key] = form[s.key] || null;
  return body;
}

// Client-side shape validation mirroring the API's validateBoxInput: a shared box
// needs a name and a digits-only dialable mailbox number. Returns an error string
// or null when valid.
export function mailboxError(name: string | undefined, mailbox: string | undefined): string | null {
  if (!name || !name.trim()) return 'Name is required';
  const m = (mailbox ?? '').trim();
  if (!m) return 'Mailbox number is required';
  if (!/^[0-9]+$/.test(m)) return 'Mailbox number must be digits only';
  return null;
}

// The minimal { add, remove } between the saved membership and the pending
// selection — the same diff the API returns from setMembers, computed locally so
// the editor can preview which phones will be (re)provisioned before saving.
export function memberDiff(before: string[], after: string[]): { add: string[]; remove: string[] } {
  const cur = new Set((before || []).map(String));
  const des = new Set((after || []).map(String));
  return {
    add: [...des].filter((x) => !cur.has(x)),
    remove: [...cur].filter((x) => !des.has(x)),
  };
}

// Human summary of a membership change for the editor's save preview, e.g.
// "2 added, 1 removed" or "No membership changes".
export function describeMemberDiff(before: string[], after: string[]): string {
  const { add, remove } = memberDiff(before, after);
  if (!add.length && !remove.length) return 'No membership changes';
  const parts: string[] = [];
  if (add.length) parts.push(`${add.length} added`);
  if (remove.length) parts.push(`${remove.length} removed`);
  return parts.join(', ');
}
